import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { MessageCircle, X, Plus, Search, ArrowLeft, Send, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { chatService, ChatRoom } from '@/services/chatService'
import { usersService, User } from '@/services/usersService'
import { usePendingCountsStore } from '@/store/pendingCountsStore'
import { useAuthStore } from '@/store/authStore'
import ChatDetailModal from '@/components/modals/ChatDetailModal'

/**
 * 관리자 플로팅 채팅 위젯 (2026-07-29 사장님)
 * - 카카오톡식 멀티 응대: 대기/진행 방 목록(방별 안읽음 배지·마지막 메시지) → 클릭 즉시 대화
 * - [+ 새 채팅]: 회원 검색(닉네임/이름/이메일) → 첫 메시지 → 관리자 발신(신청 아님, 즉시 생성)
 * - 진행 중 방이 있는 회원에겐 발신 불가 → 기존 방 열기 제안
 * - 대화 화면은 기존 ChatDetailModal 재사용(이미지·자동종료 arm·읽음 등 전부 기존 로직)
 * - 실시간 갱신: 전역 pending-counts WS 값 변화를 신호로 목록 refetch + 열림 중 30초 폴링
 */
type Screen = 'list' | 'compose'
type Tab = 'all' | 'waiting' | 'active' | 'mine'

const AdminChatWidget = () => {
  const { t } = useTranslation(['content'])
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [screen, setScreen] = useState<Screen>('list')
  const [tab, setTab] = useState<Tab>('all')
  const [rooms, setRooms] = useState<ChatRoom[]>([])
  const [loading, setLoading] = useState(false)
  const [modalRoom, setModalRoom] = useState<ChatRoom | null>(null)
  const [assigningId, setAssigningId] = useState<number | null>(null)

  // 새 채팅(compose) 상태
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [searching, setSearching] = useState(false)
  const [target, setTarget] = useState<User | null>(null)
  const [firstMessage, setFirstMessage] = useState('')
  const [chatTitle, setChatTitle] = useState('')
  const [sending, setSending] = useState(false)
  const [existingRoom, setExistingRoom] = useState<ChatRoom | null>(null)
  const [composeError, setComposeError] = useState<string | null>(null)

  const { pendingChats, unreadChatMessages } = usePendingCountsStore()
  const { admin } = useAuthStore()
  const refetchTimer = useRef<number | null>(null)
  const searchTimer = useRef<number | null>(null)

  const loadRooms = useCallback(async () => {
    try {
      setLoading(true)
      const [w, a] = await Promise.all([
        chatService.getAllChatRooms(1, 50, 'WAITING'),
        chatService.getAllChatRooms(1, 50, 'ACTIVE'),
      ])
      const merged = [...(w.data || []), ...(a.data || [])]
      // 카톡식 정렬: 대기 최상단 → 마지막 메시지 최신순
      merged.sort((x, y) => {
        if (x.status === 'WAITING' && y.status !== 'WAITING') return -1
        if (y.status === 'WAITING' && x.status !== 'WAITING') return 1
        const xt = x.last_message?.created_at || x.updated_at || x.created_at
        const yt = y.last_message?.created_at || y.updated_at || y.created_at
        return (yt || '').localeCompare(xt || '')
      })
      setRooms(merged)
    } catch (e) {
      console.error('채팅 목록 로드 실패:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  // 열려있는 동안: 최초 로드 + 30초 안전망 폴링
  useEffect(() => {
    if (!open) return
    loadRooms()
    const iv = window.setInterval(loadRooms, 30000)
    return () => clearInterval(iv)
  }, [open, loadRooms])

  // 전역 pending-counts WS 값 변화 = 새 메시지/새 방/읽음 신호 → 디바운스 refetch
  useEffect(() => {
    if (!open) return
    if (refetchTimer.current) window.clearTimeout(refetchTimer.current)
    refetchTimer.current = window.setTimeout(loadRooms, 400)
    return () => { if (refetchTimer.current) window.clearTimeout(refetchTimer.current) }
  }, [pendingChats, unreadChatMessages, open, loadRooms])

  // 외부(회원 상세페이지 등)에서 특정 회원으로 새 채팅 바로 열기 (2026-07-29 사장님)
  useEffect(() => {
    const handler = (e: Event) => {
      const u = (e as CustomEvent).detail
      if (!u?.id) return
      setOpen(true)
      setScreen('compose')
      setSearch(''); setResults([]); setExistingRoom(null); setComposeError(null); setFirstMessage('')
      setTarget(u as User)
    }
    window.addEventListener('adminChatCompose', handler)
    return () => window.removeEventListener('adminChatCompose', handler)
  }, [])

  // 회원 검색 (300ms 디바운스)
  useEffect(() => {
    if (screen !== 'compose') return
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    if (!search.trim()) { setResults([]); return }
    searchTimer.current = window.setTimeout(async () => {
      try {
        setSearching(true)
        const res = await usersService.getUsers(1, 10, search.trim())
        setResults(res.users || [])
      } catch { setResults([]) } finally { setSearching(false) }
    }, 300)
    return () => { if (searchTimer.current) window.clearTimeout(searchTimer.current) }
  }, [search, screen])

  const openRoom = async (room: ChatRoom) => {
    if (room.status === 'WAITING') {
      // 기존 배정 흐름 그대로 — 배정 후 ACTIVE로 열기
      try {
        setAssigningId(room.id)
        await chatService.assignChatRoom(room.id)
        setModalRoom({ ...room, status: 'ACTIVE' })
        loadRooms()
      } catch (e) {
        console.error('배정 실패:', e)
      } finally {
        setAssigningId(null)
      }
    } else {
      setModalRoom(room)
    }
  }

  const resetCompose = () => {
    setSearch(''); setResults([]); setTarget(null)
    setFirstMessage(''); setChatTitle(''); setExistingRoom(null); setComposeError(null)
  }

  const handleInitiate = async () => {
    if (!target || !firstMessage.trim() || sending) return
    try {
      setSending(true)
      setComposeError(null)
      setExistingRoom(null)
      const res = await chatService.initiateChat(target.id, firstMessage.trim(), chatTitle)
      if (res.created && res.room) {
        resetCompose()
        setScreen('list')
        loadRooms()
        setModalRoom(res.room)
      } else if (res.existing_room) {
        setExistingRoom(res.existing_room)
      } else {
        setComposeError(t('content:chatWidget.startFailed'))
      }
    } catch (e: any) {
      if (e?.response?.status === 429) setComposeError(t('content:chatWidget.rateLimited'))
      else setComposeError(e?.response?.data?.detail || t('content:chatWidget.startFailed'))
    } finally {
      setSending(false)
    }
  }

  const filteredRooms = rooms.filter(r => {
    if (tab === 'waiting') return r.status === 'WAITING'
    if (tab === 'active') return r.status === 'ACTIVE'
    if (tab === 'mine') return r.status === 'ACTIVE' && r.admin_id != null && r.admin_id === admin?.id
    return true
  })

  // 배지 = 안읽은 메시지 수만 (2026-07-29 사장님 — 방 개수 합산 시 상시 1 떠있던 문제)
  const totalBadge = (unreadChatMessages || 0)
  const fmtTime = (iso?: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    return d.toDateString() === now.toDateString()
      ? d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })
      : `${d.getMonth() + 1}/${d.getDate()}`
  }
  const previewOf = (r: ChatRoom) => {
    const lm = r.last_message as any
    if (!lm) return ''
    if (lm.message_type === 'IMAGE') return t('content:chatWidget.imageMessage')
    return lm.content || ''
  }
  const displayName = (r: ChatRoom) =>
    r.user_nickname && r.user_nickname !== r.user_name
      ? `${r.user_name}(${r.user_nickname})` : (r.user_name || `#${r.user_id}`)

  const tabs: { key: Tab; label: string }[] = [
    { key: 'all', label: t('content:chatWidget.tabAll') },
    { key: 'waiting', label: t('content:chatWidget.tabWaiting') },
    { key: 'active', label: t('content:chatWidget.tabActive') },
    { key: 'mine', label: t('content:chatWidget.tabMine') },
  ]

  return createPortal(
    <>
      {/* 플로팅 버튼 */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setScreen('list') }}
          className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-[9980] w-14 h-14 rounded-full
                     bg-gradient-to-br from-primary-500 to-primary-700 shadow-lg shadow-primary-500/30
                     flex items-center justify-center text-white hover:scale-105 transition-transform"
          title={t('content:chatWidget.openChat')}
        >
          <MessageCircle className="w-6 h-6" />
          {totalBadge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full
                             bg-red-500 text-white text-[11px] font-bold flex items-center justify-center
                             border-2 border-dark-900">
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
        </button>
      )}

      {/* 패널 */}
      {open && (
        <div className="fixed z-[9990] inset-0 lg:inset-auto lg:bottom-6 lg:right-6
                        lg:w-[400px] lg:h-[600px] lg:max-h-[75vh]
                        bg-[#12141f] border border-white/10
                        lg:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              {screen === 'compose' && (
                <button onClick={() => { resetCompose(); setScreen('list') }}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-dark-300">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <MessageCircle className="w-5 h-5 text-primary-400" />
              <h3 className="text-white font-semibold text-sm">
                {screen === 'compose' ? t('content:chatWidget.newChat') : t('content:chatWidget.title')}
              </h3>
            </div>
            <div className="flex items-center gap-1.5">
              {screen === 'list' && (
                <button onClick={() => { resetCompose(); setScreen('compose') }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-500/20
                                   text-primary-300 hover:bg-primary-500/30 text-xs font-medium">
                  <Plus className="w-3.5 h-3.5" />{t('content:chatWidget.newChat')}
                </button>
              )}
              <button onClick={() => setOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-dark-300 hover:text-red-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {screen === 'list' ? (
            <>
              {/* 상태 탭 */}
              <div className="flex gap-1 px-3 py-2 border-b border-white/5 shrink-0">
                {tabs.map(tb => (
                  <button key={tb.key} onClick={() => setTab(tb.key)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            tab === tb.key ? 'bg-primary-500/30 text-primary-200'
                                           : 'text-dark-400 hover:bg-white/5'}`}>
                    {tb.label}
                    {tb.key === 'waiting' && rooms.some(r => r.status === 'WAITING') && (
                      <span className="ml-1 text-red-400">●</span>
                    )}
                  </button>
                ))}
              </div>
              {/* 방 목록 */}
              <div className="flex-1 overflow-y-auto">
                {loading && rooms.length === 0 ? (
                  <div className="py-16 text-center text-dark-400 text-sm">{t('content:chatWidget.loading')}</div>
                ) : filteredRooms.length === 0 ? (
                  <div className="py-16 text-center text-dark-400 text-sm">{t('content:chatWidget.noRooms')}</div>
                ) : filteredRooms.map(r => (
                  <button key={r.id} onClick={() => openRoom(r)} disabled={assigningId === r.id}
                          className="w-full flex items-start gap-3 px-4 py-3 hover:bg-white/5 text-left
                                     border-b border-white/5 disabled:opacity-50">
                    <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-bold
                                     ${r.status === 'WAITING' ? 'bg-yellow-500/20 text-yellow-300'
                                                              : 'bg-primary-500/20 text-primary-300'}`}>
                      {(r.user_name || '?').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-white text-sm font-medium truncate">
                          {r.user_id ? (
                            <span
                              title="회원 상세로 이동"
                              onClick={(e) => {
                                e.stopPropagation()   // 행 클릭(대화 열기)과 분리
                                setOpen(false)
                                navigate(`/admin/users/${r.user_id}`)
                              }}
                              className="hover:text-primary-300 hover:underline cursor-pointer"
                            >{displayName(r)}</span>
                          ) : displayName(r)}
                        </p>
                        <span className="text-[10px] text-dark-400 shrink-0 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />{fmtTime(r.last_message?.created_at || r.updated_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className="text-xs text-dark-400 truncate">
                          {r.status === 'WAITING'
                            ? <span className="text-yellow-300">{assigningId === r.id
                                ? t('content:chatWidget.assigning') : t('content:chatWidget.waitingTap')}</span>
                            : previewOf(r)}
                        </p>
                        {r.unread_count > 0 && (
                          <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500
                                           text-white text-[10px] font-bold flex items-center justify-center">
                            {r.unread_count > 99 ? '99+' : r.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* ── 새 채팅 (회원 검색 → 첫 메시지) ── */
            <div className="flex-1 flex flex-col overflow-hidden">
              {!target ? (
                <>
                  <div className="p-3 shrink-0">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
                      <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                             placeholder={t('content:chatWidget.searchPlaceholder')}
                             className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10
                                        text-white text-sm placeholder-dark-400 focus:outline-none
                                        focus:border-primary-500/50" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {searching ? (
                      <div className="py-10 text-center text-dark-400 text-sm">{t('content:chatWidget.searching')}</div>
                    ) : !search.trim() ? (
                      <div className="py-10 text-center text-dark-400 text-sm">{t('content:chatWidget.typeToSearch')}</div>
                    ) : results.length === 0 ? (
                      <div className="py-10 text-center text-dark-400 text-sm">{t('content:chatWidget.noResults')}</div>
                    ) : results.map(u => (
                      <button key={u.id} onClick={() => setTarget(u)}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left border-b border-white/5">
                        <div className="w-9 h-9 rounded-full bg-primary-500/20 text-primary-300 shrink-0
                                        flex items-center justify-center text-xs font-bold">
                          {(u.name || u.nickname || '?').slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">
                            {u.name ? `${u.name}(${u.nickname})` : u.nickname}
                            <span className="ml-1.5 text-[10px] text-dark-500">#{u.id}</span>
                          </p>
                          <p className="text-xs text-dark-400 truncate">{u.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col p-4 gap-3 overflow-y-auto">
                  <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">
                        {target.name ? `${target.name}(${target.nickname})` : target.nickname}
                        <span className="ml-1.5 text-[10px] text-dark-500">#{target.id}</span>
                      </p>
                      <p className="text-xs text-dark-400 truncate">{target.email}</p>
                    </div>
                    <button onClick={() => { setTarget(null); setExistingRoom(null); setComposeError(null) }}
                            className="text-xs text-dark-400 hover:text-white shrink-0">
                      {t('content:chatWidget.changeTarget')}
                    </button>
                  </div>

                  {existingRoom && (
                    <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-yellow-200 text-xs mb-2">{t('content:chatWidget.existingRoomNotice')}</p>
                      <button onClick={() => { const r = existingRoom; resetCompose(); setScreen('list'); setModalRoom(r) }}
                              className="w-full py-2 rounded-lg bg-yellow-500/20 text-yellow-200 text-xs
                                         font-medium hover:bg-yellow-500/30">
                        {t('content:chatWidget.openExistingRoom')}
                      </button>
                    </div>
                  )}
                  {composeError && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
                      {composeError}
                    </div>
                  )}

                  <input value={chatTitle} onChange={e => setChatTitle(e.target.value)}
                         maxLength={100}
                         placeholder={t('content:chatWidget.titlePlaceholder')}
                         className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm
                                    placeholder-dark-400 focus:outline-none focus:border-primary-500/50" />
                  <textarea value={firstMessage} onChange={e => setFirstMessage(e.target.value)}
                            rows={5} maxLength={2000} autoFocus
                            placeholder={t('content:chatWidget.firstMessagePlaceholder')}
                            className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm
                                       placeholder-dark-400 focus:outline-none focus:border-primary-500/50 resize-none" />
                  <button onClick={handleInitiate} disabled={!firstMessage.trim() || sending || !!existingRoom}
                          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500
                                     hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold">
                    <Send className="w-4 h-4" />
                    {sending ? t('content:chatWidget.starting') : t('content:chatWidget.startChat')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 대화 = 기존 ChatDetailModal 재사용 (이미지/자동종료/읽음 전부 기존 로직) */}
      <ChatDetailModal
        isOpen={!!modalRoom}
        onClose={() => { setModalRoom(null); if (open) loadRooms() }}
        room={modalRoom}
        onUpdate={loadRooms}
      />
    </>,
    document.body
  )
}

export default AdminChatWidget
